import { FC, ReactNode, useState } from 'react';

import { CardmarketProductLink } from '../../../../components/shared/CardmarketProductLink';
import { Badge } from '../../../../components/ui/Badge';
import { CartonProduct } from '../../../../types';

const DEFAULT_PX_OFFSET = 16;

interface Props {
    product: CartonProduct | null;
    importName?: string | null;
    sourceCode?: string | null;
    quantity?: string | null;
    statusIcon?: ReactNode;
}

export const BulkOfferProductCell: FC<Props> = ({
    product,
    importName,
    sourceCode,
    quantity,
    statusIcon,
}) => {
    const [preview, setPreview] = useState<{
        product: CartonProduct;
        offset: { x: number; y: number };
    } | null>(null);

    const showPreview = (
        previewProduct: CartonProduct,
        offset: { x: number; y: number },
    ) => setPreview({ product: previewProduct, offset });

    return (
        <>
            {preview && (
                <img
                    data-testid="product-image-preview"
                    className="pointer-events-none fixed z-[130] h-[350px] w-[250px] rounded-2xl"
                    style={{
                        top: `${preview.offset.y + DEFAULT_PX_OFFSET}px`,
                        left: `${preview.offset.x + DEFAULT_PX_OFFSET}px`,
                    }}
                    src={preview.product.image}
                    alt={preview.product.image_alt}
                />
            )}
            <div className="flex min-w-0 gap-2.5">
                <div className="relative h-14 w-10 shrink-0 overflow-hidden rounded-md bg-muted">
                    {product ? (
                        <img
                            src={product.image}
                            alt={product.image_alt}
                            className="h-full w-full object-contain"
                            data-testid="offer-draft-product-image"
                            loading="lazy"
                            onTouchStart={(event) => {
                                event.stopPropagation();
                                showPreview(product, {
                                    x: event.touches[0].clientX,
                                    y: event.touches[0].clientY,
                                });
                            }}
                            onMouseMove={(event) =>
                                showPreview(product, {
                                    x: event.clientX,
                                    y: event.clientY,
                                })
                            }
                            onMouseOut={() => setPreview(null)}
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center border border-dashed border-muted-foreground/40 px-1 text-center text-[10px] text-muted-foreground">
                            Без товару
                        </div>
                    )}
                    {quantity && (
                        <span className="absolute top-0 left-0 rounded-br-md bg-foreground/70 px-1 text-[10px] leading-4 font-semibold text-background">
                            {quantity}
                        </span>
                    )}
                </div>
                <div className="min-w-0">
                    <div className="flex items-start gap-1.5">
                        {statusIcon}
                        <p className="line-clamp-2 text-sm font-semibold">
                            {product?.name ??
                                importName ??
                                'Потрібно обрати товар'}
                        </p>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {product && (
                            <Badge
                                className="px-1.5 text-[11px]"
                                data-testid="offer-draft-expansion-tag"
                            >
                                {product.expansion_tag}
                            </Badge>
                        )}
                        {product && (
                            <Badge
                                variant="outline"
                                className="px-1.5 text-[11px]"
                                data-testid="offer-draft-language"
                            >
                                {product.language}
                            </Badge>
                        )}
                        {product?.cardmarket_url && (
                            <CardmarketProductLink
                                href={product.cardmarket_url}
                            />
                        )}
                    </div>
                    {sourceCode && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            Код: {sourceCode}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};
